// @ts-nocheck

declare const __BLISSNET_BUILD_SHA__: string;

function resolveBuildSha(): string {
  try {
    if (typeof __BLISSNET_BUILD_SHA__ === 'string' && __BLISSNET_BUILD_SHA__) {
      return __BLISSNET_BUILD_SHA__;
    }
  } catch {
    // define may be unavailable in some test environments
  }
  try {
    const fromVite =
      (import.meta as any)?.env?.VITE_BUILD_SHA ||
      (import.meta as any)?.env?.VITE_GIT_COMMIT;
    if (fromVite) return String(fromVite).slice(0, 7);
  } catch {
    // ignore
  }
  return 'local';
}

const BLISSNET_BUILD_SHA = resolveBuildSha();

if (typeof window !== 'undefined') {
  window.__BLISSNET_BUILD_SHA__ = BLISSNET_BUILD_SHA;
  // Legacy alias kept for older diagnostics
  window.__BLISSNET_BUILD_VERSION__ = BLISSNET_BUILD_SHA;
  if (import.meta.env.DEV) {
    console.info(`[BLISSNET_BUILD] ${BLISSNET_BUILD_SHA}`);
  }
}

declare global {
  interface Window {
    __BLISSNET_BUILD_SHA__: string;
    __BLISSNET_BUILD_VERSION__: string;
    __pi_run3?: () => Promise<void>; // V316.7: DEV-only Play Integrity 3-test runner
  }
}

// V166.12: Removed nuclear diagnostic test (production cleanup)

// V157.7-ABSOLUTE: Install global fetch interceptor FIRST
import './utils/globalFetchDeduplicator';

// V164.2: Install API redirect interceptor to fix 404s
import { installApiRedirectInterceptor } from './utils/apiRedirectInterceptor';
installApiRedirectInterceptor();

// Stale chunk recovery: reload once on dynamic import / chunk load failure (e.g. after deploy)
(function initChunkErrorRecovery() {
  if (typeof window === 'undefined' || import.meta.env.DEV) return;

  const CHUNK_RELOAD_KEY = 'chunk_reload_ts';
  const COOLDOWN_MS = 10_000;

  function shouldReloadForChunkError(): boolean {
    const now = Date.now();
    let last = 0;
    try {
      last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || '0');
    } catch {
      // iOS/WKWebView can throw if storage is unavailable/restricted during early boot.
      // Treat as "no recent reload" so the app can still recover.
      last = 0;
    }
    if (now - last < COOLDOWN_MS) return false;
    try {
      sessionStorage.setItem(CHUNK_RELOAD_KEY, String(now));
    } catch {
      // Ignore storage failures; reload gating just becomes in-memory for this boot.
    }
    return true;
  }

  function hardReload(): void {
    const url = new URL(window.location.href);
    url.searchParams.set('v', String(Date.now()));
    window.location.replace(url.toString());
  }

  function tryRecoverChunkError(): void {
    if (!shouldReloadForChunkError()) {
      console.warn('Chunk load error: skipped reload (cooldown). Reload manually if the app is broken.');
      return;
    }
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then((regs) => {
        Promise.all(regs.map((r) => r.unregister()))
          .then(() => hardReload())
          .catch(() => hardReload());
      });
    } else {
      hardReload();
    }
  }

  function isChunkError(msg: string): boolean {
    const m = (msg || '').toLowerCase();
    return (
      m.includes('failed to fetch dynamically imported module') ||
      m.includes('importing a module script failed') ||
      m.includes('chunkloaderror') ||
      m.includes('loading chunk')
    );
  }

  window.addEventListener('vite:preloadError', ((e: Event & { preventDefault?: () => void }) => {
    e?.preventDefault?.();
    tryRecoverChunkError();
  }) as EventListener);

  window.addEventListener('unhandledrejection', (e) => {
    const msg = String((e as { reason?: { message?: string }; reason?: unknown })?.reason?.message ?? (e as { reason?: unknown })?.reason ?? '');
    if (isChunkError(msg)) tryRecoverChunkError();
  });

  window.addEventListener('error', (e) => {
    if (e.target instanceof HTMLImageElement) return;
    const msg = String((e as ErrorEvent).message ?? '');
    if (isChunkError(msg)) tryRecoverChunkError();
  });
})();

/**
 * Legacy SW cleanup: unregister non-Firebase service workers and delete BlissNet
 * app shell caches (blissnet-cache-*, runtime-cache). Keep firebase-messaging-sw
 * and profile-photo caches. Reload once after cleanup so HTML/assets refresh.
 */
(function initLegacyServiceWorkerCleanup() {
  if (typeof window === 'undefined' || import.meta.env.DEV) return;
  if (!('serviceWorker' in navigator)) return;

  const RELOAD_GUARD = 'blissnet_legacy_sw_cleanup_reload';

  (async () => {
    let cleaned = false;
    try {
      const regs = await navigator.serviceWorker.getRegistrations();
      for (const reg of regs) {
        const scriptURL =
          reg.active?.scriptURL ||
          reg.waiting?.scriptURL ||
          reg.installing?.scriptURL ||
          '';
        if (scriptURL.includes('firebase-messaging-sw.js')) {
          // Keep push SW; still ask it to check for updates.
          try {
            await reg.update();
          } catch {
            // ignore
          }
          continue;
        }
        await reg.unregister();
        cleaned = true;
      }
    } catch {
      // ignore registration errors
    }

    try {
      if ('caches' in window) {
        const keys = await caches.keys();
        for (const key of keys) {
          if (
            key === 'runtime-cache' ||
            key.startsWith('blissnet-cache') ||
            key.startsWith('workbox-')
          ) {
            await caches.delete(key);
            cleaned = true;
          }
        }
      }
    } catch {
      // ignore cache errors
    }

    if (!cleaned) return;
    try {
      if (sessionStorage.getItem(RELOAD_GUARD) === '1') return;
      sessionStorage.setItem(RELOAD_GUARD, '1');
    } catch {
      // If sessionStorage unavailable, skip reload to avoid loops.
      return;
    }
    window.location.reload();
  })();
})();

import React from 'react';
import ReactDOM from 'react-dom/client';
import './i18n';
import '@/utils/lovableCompat';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { DirectedToaster as Toaster } from '@/components/ui/sonner';

// V212.1: Native Android OAuth Deep Link Handler
import { App as CapApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { redactInviteSensitiveUrl } from './utils/safeInviteLogging';
import { supabase } from './lib/supabase';
import { exchangeCodeForSession } from './lib/auth-helpers';
import {
  blockPrematureIOSOAuthClose,
  clearIOSOAuthLock
} from './lib/iosOAuthLock';
import { logOAuthCallbackReceived, logSupabaseSignInResult } from './utils/authDiagLogger';
import { ChatMessageCache } from './services/ChatMessageCache';
// import { backgroundPhotoUpload } from './services/backgroundPhotoUpload'; // Removed - consolidated into PhotoService
import { logger } from './utils/productionLogger';
import { getBootstrapIsDark } from '@/utils/themePreference';
import './index.css';

const IOS_OAUTH_BUILD_PROOF =
  '__BLISSNET_IOS_OAUTH_PENDING__|blissnet_ios_oauth_lock|blissnet_ios_oauth_close_blocked|Browser.close BLOCKED';
if (typeof window !== 'undefined') {
  (window as any).__BLISSNET_IOS_OAUTH_BUILD_PROOF__ = IOS_OAUTH_BUILD_PROOF;
}

// Cleanup scripts and optimizations disabled for stability

// V168.5: Console cleanup - suppress verbose debug logs in production
// Only allow critical logs (errors, warnings, and important real-time notifications)
(() => {
  const originalLog = console.log.bind(console);
  const isDev = import.meta.env.DEV;

  // In production, only allow critical logs:
  // - Errors (❌)
  // - Critical warnings (⚠️ with specific patterns)
  // - Real-time notifications (🔔 V167.REALTIME, 🔔 V167.ACCEPT)
  // - Diagnostic logs (🔍 V167.DISCOVER) - CRITICAL for debugging Discover page issues
  // Auth failures use console.error (not filtered). AUTH_DIAG success/lifecycle is DEV-only.
  // In development, allow all logs
  const allowRegex = isDev ? /.*/ : /^(❌|⚠️.*(Failed|Error|timeout|critical)|🔔 V167\.(REALTIME|ACCEPT)|🔍 V167\.DISCOVER|═══════════════════════════════════════════════════════════)/;

  console.log = (...args: any[]) => {
    try {
      const first = args[0];
      if (typeof first === 'string' && !allowRegex.test(first)) {
        return; // suppress non-matching logs
      }
    } catch { }
    originalLog(...args);
  };
})();

class RouterErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): { hasError: boolean } {
    if (error.message.includes('router') || error.message.includes('Router')) {
      return { hasError: true };
    }
    return { hasError: false };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    logger.error('Router error boundary caught error:', error, errorInfo);
    // V161.FIX: Don't auto-reload - let user decide, prevents infinite loop
    console.warn('Router error caught:', error.message);
    // Only reload if it's a critical router error, not scroll restoration issues
    if (error.message.includes('router') && !error.message.includes('scroll')) {
      console.warn('Critical router error, attempting recovery...');
      setTimeout(() => {
        this.setState({ hasError: false });
        window.location.reload();
      }, 2000);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-xl font-semibold mb-2">Something went wrong</h2>
            <p className="text-gray-600 mb-4">There was an error with the application routing.</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// 📱 MOBILE PERFORMANCE OPTIMIZATIONS
import { initializeMobileOptimizations, monitorMobilePerformance } from '@/utils/touchOptimization';
import { mobileCache } from '@/services/mobileCache';
import { initializeMobileViewport } from '@/utils/mobileViewport';

// 🚫 SUPPRESS 404 ERRORS: Enhanced global error handler to suppress Cloudinary 404s
window.addEventListener('error', (e) => {
  if (e.target instanceof HTMLImageElement) {
    const src = e.target.src;

    // Suppress 404 for נטלי פלדבאום's known missing image
    if (src.includes('fd3d730e-1b0e-4948-a6bc-bcaaf7f1d9a1')) {
      if (import.meta.env.DEV) {
        console.log(`🔇 Global suppressed 404: נטלי פלדבאום's missing image`);
      }
      e.preventDefault();
      e.stopImmediatePropagation();
      return false;
    }

    // Suppress all Cloudinary 404 errors
    if (src.includes('cloudinary.com')) {
      if (import.meta.env.DEV) {
        console.log(`🔇 Global suppressed Cloudinary 404: ${src.split('/').pop()}`);
      }
      e.preventDefault();
      e.stopImmediatePropagation();
      return false;
    }
  }
}, true);

// 🔇 ENHANCED ERROR SUPPRESSION: Intercept network errors at console level
const originalConsoleError = console.error;
console.error = (...args) => {
  const message = args.join(' ');

  // Suppress realtime subscription noise (common on flaky connections / blocked websockets)
  if (
    message.includes('Channel error - subscription failed') ||
    message.includes('Global subscription error')
  ) {
    // Keep visible in DEV if needed
    if (import.meta.env.DEV) {
      console.log('🔇 Console suppressed: Supabase realtime subscription error');
    }
    return;
  }

  // Suppress specific 404 errors
  if (message.includes('404') && message.includes('fd3d730e-1b0e-4948-a6bc-bcaaf7f1d9a1')) {
    if (import.meta.env.DEV) {
      console.log(`🔇 Console suppressed: נטלי פלדבאום's 404 error`);
    }
    return;
  }

  // Suppress general Cloudinary 404s
  if (message.includes('404') && message.includes('cloudinary.com')) {
    if (import.meta.env.DEV) {
      console.log(`🔇 Console suppressed: Cloudinary 404 error`);
    }
    return;
  }

  // Suppress WhatsApp API errors
  if (message.includes('api.whatsapp.com') || message.includes('whatsapp') && message.includes('404')) {
    if (import.meta.env.DEV) {
      console.log(`🔇 Console suppressed: WhatsApp API error`);
    }
    return;
  }

  // Suppress manifest.json errors
  if (message.includes('manifest.json') && message.includes('404')) {
    if (import.meta.env.DEV) {
      console.log(`🔇 Console suppressed: Manifest 404 error`);
    }
    return;
  }

  // Allow all other errors through
  originalConsoleError.apply(console, args);
};

// V163.6.8: Removed conflicting fetch override - globalFetchDeduplicator handles all interception

// 🚀 V208.P.2: Initialize mobile viewport optimizations - ENABLE ZOOM for accessibility
initializeMobileViewport({
  preventZoom: false, // ✅ Enable zoom for accessibility (Lighthouse requirement)
  useSafeAreas: true,
  fixKeyboardIssues: true,
  enablePWAMode: true,
  statusBarStyle: 'black-translucent',
  themeColor: '#e91e63'
});

// V274.1: Android debug helper for photo upload issues
if (Capacitor.getPlatform() === 'android') {
  window.onerror = (msg, url, line, col, error) => {
    console.error('[ANDROID_ERROR]', { msg, url, line, col, error });
    return false;
  };

  window.addEventListener('unhandledrejection', (event) => {
    console.error('[UNHANDLED_PROMISE]', event.reason);
  });

  // Enhanced logging for photo upload debugging
  const originalConsoleLog = console.log.bind(console);
  console.log = (...args: any[]) => {
    const firstArg = args[0];
    // Always show V274.1 logs for Android photo debugging
    if (typeof firstArg === 'string' && firstArg.includes('[V274.1')) {
      originalConsoleLog(...args);
      return;
    }
    // Call original log (which may filter in production)
    originalConsoleLog(...args);
  };
}

// V335.4 & V337.6: Early theme sync before React - prevents status bar flash on app start
// V350: Default follows system (prefers-color-scheme); explicit dark/light from blissnet-theme-mode
const isNative = Capacitor.isNativePlatform();
const platform = isNative ? Capacitor.getPlatform() : 'web';

let initialDark = false;
try {
  initialDark = getBootstrapIsDark();
} catch {
  initialDark =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches;
}

if (initialDark) {
  document.documentElement.classList.add('dark');
} else {
  document.documentElement.classList.remove('dark');
}
const themeColor = initialDark ? '#1a1a2e' : '#FFFFFF';
let themeMeta = document.querySelector('meta[name="theme-color"]');
if (!themeMeta) {
  themeMeta = document.createElement('meta');
  themeMeta.setAttribute('name', 'theme-color');
  document.head.appendChild(themeMeta);
}
themeMeta.setAttribute('content', themeColor);

if (import.meta.env.DEV) {
  console.log('[main.tsx] Theme bootstrap', {
    platform,
    initialDark,
    rootClasses: Array.from(document.documentElement.classList),
    dataTheme: (document.documentElement as HTMLElement).getAttribute('data-theme') || null,
  });
}

// V313: CRITICAL - Add platform-android class EARLY (before React render) to both html and body
// This ensures safe-area CSS variables are applied immediately and persist across route changes
if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android') {
  document.documentElement.classList.add('platform-android');
  document.body.classList.add('platform-android');
  console.log('[V313] ✅ Added platform-android class to html AND body for safe-area enforcement');

  // V335.4 / V601: Early StatusBar sync — skip deprecated Window.setStatusBarColor
  // bridge calls on every Android version (UA-based detection is unsafe).
  Promise.all([
    import('@/utils/androidPlatform'),
    import('@capacitor/status-bar'),
  ]).then(async ([{ shouldSkipNativeSystemBarManipulation }, { StatusBar, Style }]) => {
    if (!shouldSkipNativeSystemBarManipulation()) {
      await StatusBar.setBackgroundColor({ color: themeColor });
    }
    await StatusBar.setStyle({ style: initialDark ? Style.Dark : Style.Light });
  });

  // V313: Set initial --app-height immediately (before React useEffect runs)
  const setInitialAppHeight = () => {
    const height = window.innerHeight;
    document.documentElement.style.setProperty('--app-height', `${height}px`);
    console.log('[V313] Initial --app-height set:', height);
  };
  setInitialAppHeight();

  // V313: Re-apply on orientation change (early handler, before React)
  window.addEventListener('orientationchange', () => {
    setTimeout(setInitialAppHeight, 100); // Small delay for orientation to complete
  });
}

// V337: Add platform-ios class EARLY (before React render) for CSS guards
if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
  document.documentElement.classList.add('platform-ios');
  document.body.classList.add('platform-ios');
  console.log('[V337] ✅ Added platform-ios class to html AND body');
}

// Start performance monitoring in development
if (import.meta.env.DEV) {
  monitorMobilePerformance();

  // Expose mobile cache for debugging
  (window as any).mobileCache = mobileCache;
  logger.debug('📱 Mobile cache available: window.mobileCache');

  // Expose device info for debugging
  import('./utils/mobileViewport').then(module => {
    (window as any).getDeviceInfo = module.getDeviceInfo;
    logger.debug('📱 Device info available: window.getDeviceInfo()');
  });

  // 🎯 EXPOSE CLOUDINARY FIX TOOLS (DEV ONLY)
  if (import.meta.env.DEV) {
    import('./utils/implementCloudinaryFix').then(module => {
      (window as any).cloudinaryFix = module.default;
      logger.debug('🎯 Cloudinary fix tools available: window.cloudinaryFix');
      logger.debug('   Quick test: cloudinaryFix.quickTest()');
      logger.debug('   Run all: cloudinaryFix.runAllSteps()');
    });

    // 🧪 EXPOSE CORRECTED MAPPING TEST
    import('./utils/image').then(module => {
      (window as any).testCorrectedMappings = module.testCorrectedImageMappings;
      logger.debug('🧪 CORRECTED MAPPING TEST: window.testCorrectedMappings()');
    });
  }
}

// 🚀 CACHE PERFORMANCE MONITORING: Add global cache monitoring tools
import('./utils/navigationMonitor').then(module => {
  const { NavigationMonitor } = module;

  (window as any).blissnetCache = {
    showStats: () => {
      NavigationMonitor.showDashboard();
    },

    clearCache: () => {
      NavigationMonitor.clearHistory();
    },

    testNavigation: () => {
      // Silent navigation test
    },

    getHistory: () => NavigationMonitor.getHistory()
  };

  // 🧹 DEBUG CONTROLS: Add verbose logging toggle (production clean)
  (window as any).blissnetDebug = {
    enableVerbose: () => {
      localStorage.setItem('blissnet-debug', 'true');
    },
    disableVerbose: () => {
      localStorage.removeItem('blissnet-debug');
    },
    isEnabled: () => localStorage.getItem('blissnet-debug') === 'true',
    status: () => {
      const enabled = localStorage.getItem('blissnet-debug') === 'true';
      return enabled ? 'VERBOSE' : 'CLEAN';
    }
  };
});

// 📱 MOBILE PERFORMANCE: Re-enable animations for premium mobile experience
import './styles/whatsapp-animations.css';

// Initialize chat message cache system
ChatMessageCache.initialize();

// Background photo upload service consolidated into PhotoService
// backgroundPhotoUpload.initialize().catch(error => {
//   console.error('Failed to initialize background photo upload:', error);
// });

// Debug functions for testing (DEV ONLY)
if (import.meta.env.DEV && typeof window !== 'undefined') {
  (window as any).debugCache = {
    getMessages: (chatId: string) => ChatMessageCache.getMessages(chatId),
    getCacheStats: () => ChatMessageCache.getCacheStats(),
    clearAll: () => ChatMessageCache.clearAll(),
    hasCache: (chatId: string) => ChatMessageCache.hasCache(chatId)
  };
}

// V250: DEV guard - block direct URL.createObjectURL calls outside objectUrl.ts
if (import.meta.env.DEV && typeof window !== 'undefined') {
  const originalCreateObjectURL = URL.createObjectURL.bind(URL);
  URL.createObjectURL = function (obj: Blob | MediaSource) {
    const stack = new Error().stack || '';
    // Check if call is from objectUrl.ts (allowed)
    const isFromObjectUrl = stack.includes('objectUrl.ts');
    if (!isFromObjectUrl) {
      const stackPreview = stack.split('\n').slice(0, 8).join('\n');
      console.error('🚫 V250 BLOCKED: Direct URL.createObjectURL call detected!', {
        type: obj?.type,
        size: (obj as Blob)?.size,
        stack: stackPreview,
      });
      console.error('🚫 V250: All createObjectURL calls must go through src/lib/objectUrl.ts');
      // In DEV, we log but don't throw to avoid breaking builds - but it should be fixed
      // throw new Error('Direct URL.createObjectURL call blocked - use objectUrl.ts wrappers');
    }
    return originalCreateObjectURL(obj);
  };
  console.log('✅ V250: DEV guard installed - direct createObjectURL calls will be logged');
}

// Debug functions for photo workflow (development only)
if (import.meta.env.DEV && typeof window !== 'undefined') {
  (window as any).debugPhotos = {
    // getQueueStatus: () => backgroundPhotoUpload.getQueueStatus(), // Consolidated into PhotoService
    getLocalPhoto: (userId: string) => {
      const stored = localStorage.getItem(`blissnet_selfie_${userId}`);
      return stored ? JSON.parse(stored) : null;
    },
    clearLocalPhoto: (userId: string) => {
      localStorage.removeItem(`blissnet_selfie_${userId}`);
    },
    listLocalPhotos: () => {
      const keys = Object.keys(localStorage);
      return keys.filter(key => key.startsWith('blissnet_selfie_'));
    }
  };

  // Import and expose match system validation functions
  import('./utils/validateMatchSystem').then(module => {
    (window as any).validateMatchSystem = module.validateMatchSystem;
    (window as any).quickMatchSystemStatus = module.quickMatchSystemStatus;
    logger.debug('✅ Match system validation loaded. Run window.validateMatchSystem() for full test.');
  });
}

// V212.2: Register deep link handler BEFORE React render with enhanced logging and event-based navigation
// V284.2: Enhanced to handle invite deep links (navigate to /invite/:code internally)
if (Capacitor.isNativePlatform()) {
  const redactSensitiveCallbackUrl = (incomingUrl: string): string =>
    redactInviteSensitiveUrl(incomingUrl);

  const closeIOSBrowserSafeMain = async (reason: string): Promise<void> => {
    if (Capacitor.getPlatform() !== 'ios') return;
    console.error('[AUTH] Browser.close candidate reason=' + reason);
    if (reason !== 'oauth_success_callback' && blockPrematureIOSOAuthClose('main_closeIOSBrowserSafeMain_' + reason)) {
      return;
    }
    try {
      const { Browser } = await import('@capacitor/browser');
      await Browser.close();
    } catch (closeErr) {
      console.warn('[V334 Deep Link] ⚠️ Browser.close() skipped (already dismissed):', closeErr);
    }
  };

  CapApp.addListener('appUrlOpen', async ({ url }) => {
    const redactedUrl = redactSensitiveCallbackUrl(String(url));
    // V407-DIAG: literal first-line proof — runs before any URL parsing so a malformed
    // URL or thrown ctor cannot mask the bridge fire. URL is redacted for sensitive tokens/codes.
    console.error('[BLISSNET-JS] appUrlOpen RAW fired raw=' + redactedUrl);
    try {
      localStorage.setItem('blissnet_last_appUrlOpen_raw', redactedUrl);
      localStorage.setItem('blissnet_last_appUrlOpen_at', new Date().toISOString());
    } catch {}
    if (import.meta.env.DEV) {
      try {
        const { toast } = await import('sonner');
        toast.success('iOS callback received', { duration: 8000 });
      } catch {}
    }

    // V406-DIAG: HIGH-VISIBILITY proof that the JS bridge fired.
    // console.error survives the production console.log filter applied earlier in main.tsx.
    // Toast gives a visible-on-device confirmation. localStorage gives a post-hoc breadcrumb.
    // We deliberately log only scheme/host/hasCode — never the auth code itself.
    try {
      const _u = new URL(url);
      const _hasCode = _u.searchParams.has('code');
      const _host = _u.hostname.toLowerCase();
      const _isInvite =
        ((_host === 'blissnet.app' || _host === 'www.blissnet.app') &&
          _u.pathname.startsWith('/invite/')) ||
        (_u.protocol === 'blissnet:' && _host === 'invite');
      const _safePath = _isInvite
        ? '/invite/[REDACTED]'
        : _u.pathname.replace(/\/invite\/[^/?#]+/i, '/invite/[REDACTED]');
      const _marker = `[BLISSNET-JS] appUrlOpen fired scheme=${_u.protocol.replace(':', '')} host=${_u.hostname} hasCode=${_hasCode} route=${_isInvite ? 'invite_route' : 'other'}`;
      console.error(_marker);
      // V410: AUTHFLOW probe — single grep prefix end-to-end so the next CTO can grep one term.
      // Never log raw invite path segments.
      console.error(
        `[AUTHFLOW] appUrlOpen raw protocol=${_u.protocol} host=${_u.hostname} ` +
        `path=${_safePath} hasSearch=${!!_u.search} hasHash=${!!_u.hash} ` +
        `hasCode=${_hasCode} hasError=${_u.searchParams.has('error')}`
      );
      try { localStorage.setItem('blissnet_last_appUrlOpen', `${_marker} @ ${new Date().toISOString()}`); } catch {}
      if (import.meta.env.DEV) {
        try {
          const { toast } = await import('sonner');
          toast.error(_marker, { duration: 8000 });
        } catch {}
      }
    } catch (_diagErr) {
      console.error('[BLISSNET-JS] appUrlOpen fired but URL parse failed:', _diagErr);
      console.error('[AUTHFLOW] appUrlOpen parse failed', _diagErr);
    }

    console.log('[V284.2 Deep Link] ========================================');
    console.log('[V284.2 Deep Link] URL received:', redactedUrl);
    console.log('[V284.2 Deep Link] Timestamp:', new Date().toISOString());

    try {
      // Parse the URL to extract the code
      const parsedUrl = new URL(url);

      // V284.2: Handle invite deep links (https://blissnet.app/invite/:code OR blissnet://invite/:code)
      const normalizedHost = parsedUrl.hostname.toLowerCase();
      const isInviteHost = normalizedHost === 'blissnet.app' || normalizedHost === 'www.blissnet.app';
      const isHttpsInvite = isInviteHost && parsedUrl.pathname.startsWith('/invite/');
      const isCustomSchemeInvite = parsedUrl.protocol === 'blissnet:' && parsedUrl.hostname === 'invite';

      if (isHttpsInvite || isCustomSchemeInvite) {
        let inviteCode: string | null = null;

        if (isHttpsInvite) {
          // Extract from path: /invite/abc123
          const match = parsedUrl.pathname.match(/^\/invite\/([^/?#]+)/);
          inviteCode = match ? match[1] : null;
        } else if (isCustomSchemeInvite) {
          // Extract from custom scheme: blissnet://invite/abc123
          inviteCode = parsedUrl.pathname.replace(/^\//, '');
        }

        if (inviteCode) {
          console.info('NATIVE_LAUNCH_ROUTE', {
            route: 'invite_route',
            action: 'deeplink_received',
          });

          // V284.2: Dispatch navigation event to route to /invite/:code internally
          // This will trigger InviteRedirect.tsx which handles the full invite flow
          const event = new CustomEvent('v284-invite-deeplink', {
            detail: { inviteCode }
          });
          window.dispatchEvent(event);
          console.info('NATIVE_LAUNCH_ROUTE', {
            route: 'invite_route',
            action: 'deeplink_navigating',
          });
          return;
        } else {
          console.warn('NATIVE_LAUNCH_ROUTE', { route: 'invite_route', action: 'deeplink_missing_code' });
        }
      }

      // OAuth native callback supported forms:
      // 1) com.blissnet.app://auth-callback?code=... (PKCE)
      // 2) com.blissnet.app://auth-callback#access_token=...&refresh_token=... (implicit fallback)
      // 3) com.blissnet.app:///auth-callback?code=... (path form)
      // 4) https://blissnet.app/auth-callback?code=... (Universal Link form)
      const isOAuthCallbackTarget =
        parsedUrl.hostname === 'auth-callback' ||
        (parsedUrl.protocol === 'com.blissnet.app:' &&
          /^\/auth-callback\/?$/.test(parsedUrl.pathname || '')) ||
        (
          parsedUrl.protocol === 'https:' &&
          parsedUrl.hostname.toLowerCase() === 'blissnet.app' &&
          /^\/auth-callback\/?$/.test(parsedUrl.pathname || '')
        );

      const hashParams = new URLSearchParams((parsedUrl.hash || '').replace(/^#/, ''));
      const hasHashTokens = !!(hashParams.get('access_token') && hashParams.get('refresh_token'));
      const hasOAuthQueryParams =
        parsedUrl.searchParams.has('code') ||
        parsedUrl.searchParams.has('error') ||
        parsedUrl.searchParams.has('error_description');
      const isNativeOAuthCallback = isOAuthCallbackTarget && (hasOAuthQueryParams || hasHashTokens);

      // V410: AUTHFLOW probe — classifies URL the same way the parser does.
      console.error(
        `[AUTHFLOW] callback classified oauth=${isNativeOAuthCallback} ` +
        `hasCode=${parsedUrl.searchParams.has('code')} ` +
        `hasHashTokens=${hasHashTokens} ` +
        `hasError=${parsedUrl.searchParams.has('error') || parsedUrl.searchParams.has('error_description')} ` +
        `proto=${parsedUrl.protocol} host=${parsedUrl.hostname} path=${parsedUrl.pathname}`
      );

      if (isNativeOAuthCallback) {
        const code = parsedUrl.searchParams.get('code');
        const oauthError = parsedUrl.searchParams.get('error');
        const oauthErrorDescription = parsedUrl.searchParams.get('error_description');
        const idToken = parsedUrl.searchParams.get('id_token');
        const hasCode = !!code;
        const hasError = !!(oauthError || oauthErrorDescription);
        console.error(`[AUTH] callback shape code=${hasCode} hashTokens=${hasHashTokens}`);
        console.error(`[AUTH] deep link received hasCode=${hasCode} hasError=${hasError}`);
        const params: Record<string, string> = {};
        parsedUrl.searchParams.forEach((v, k) => { params[k] = v; });

        // V331: Auth flow instrumentation
        logOAuthCallbackReceived(redactedUrl, !!code, !!idToken, params);

        console.log('[V212 Deep Link] Code extracted:', code ? '✅ YES' : '❌ NO');
        console.log('[V212 Deep Link] Code length:', code?.length || 0);

        if (oauthError || oauthErrorDescription) {
          const errMsg = oauthErrorDescription || oauthError || 'OAuth callback returned an unknown error';
          clearIOSOAuthLock('oauth_error_callback');
          console.error('[V212 Deep Link] ❌ OAuth provider returned error in callback');
          console.error('[V212 Deep Link] Error:', oauthError);
          console.error('[V212 Deep Link] Description:', oauthErrorDescription);
          console.error('[V212 Deep Link] Full URL:', redactedUrl);
          logSupabaseSignInResult(false, `oauth_callback_error:${errMsg}`);

          // Keep behavior iOS-safe: ensure in-app browser isn't left open on provider error.
          if (Capacitor.getPlatform() === 'ios') {
            await closeIOSBrowserSafeMain('oauth_error_callback');
          }
          return;
        }

        if (!code) {
          const accessToken = hashParams.get('access_token');
          const refreshToken = hashParams.get('refresh_token');

          if (accessToken && refreshToken) {
            console.error('[AUTH] setSession from hash start');
            const { data: hashSessionData, error: hashSessionError } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken
            });
            console.error(`[AUTH] setSession from hash success=${!hashSessionError} error=${hashSessionError?.message || ''}`);

            if (hashSessionError || !hashSessionData.session) {
              clearIOSOAuthLock('exchange_failed_no_code_hash_session_failed');
              logSupabaseSignInResult(false, hashSessionError?.message || 'hash_session_missing');
              return;
            }

            const hashUser = hashSessionData.session.user;
            logSupabaseSignInResult(true);
            clearIOSOAuthLock('exchange_success_hash_session');

            console.log('[V212 Deep Link] ✅ SUCCESS: Session established from hash tokens');
            console.log('[V212 Deep Link] User ID:', hashUser.id);
            console.log('[V212 Deep Link] User email:', hashUser.email);
            console.log('[V212 Deep Link] ========================================');

            try {
              const { ensureUserProfile } = await import('./lib/auth-helpers');
              const profileCreated = await ensureUserProfile(hashUser.id, hashSessionData.session);
              console.log('[V212] Profile creation result:', profileCreated);
              if (!profileCreated) {
                setTimeout(async () => {
                  const retryResult = await ensureUserProfile(hashUser.id, hashSessionData.session);
                  console.log('[V212] Retry result:', retryResult);
                }, 2000);
              }
            } catch (err) {
              console.error('[V212] ❌ Profile creation EXCEPTION:', err);
            }

            const event = new CustomEvent('v212-auth-success', {
              detail: {
                userId: hashSessionData.session.user.id,
                email: hashSessionData.session.user.email,
                session: hashSessionData.session
              }
            });
            window.dispatchEvent(event);
            console.log('[V212 Deep Link] Auth success event dispatched - app will handle navigation');

            if (Capacitor.getPlatform() === 'ios') {
              await closeIOSBrowserSafeMain('oauth_success_callback');
              console.log('[V334 Deep Link] ✅ In-app browser closed after successful OAuth');
            }
            return;
          }

          clearIOSOAuthLock('exchange_failed_no_code');
          console.error('[V212 Deep Link] ❌ ERROR: No authorization code in callback URL');
          logSupabaseSignInResult(false, 'no_code_in_callback');
          return;
        }

        console.log('[V212 Deep Link] Starting PKCE code exchange...');
        const startTime = Date.now();

        // Exchange code for session (PKCE flow)
        const { data, error } = await exchangeCodeForSession(code);

        const duration = Date.now() - startTime;
        console.log('[V212 Deep Link] Exchange completed in', duration, 'ms');

        if (error) {
          clearIOSOAuthLock('exchange_failed');
          console.error('[V212 Deep Link] ❌ ERROR: Session exchange failed');
          console.error('[V212 Deep Link] Error message:', error.message);
          console.error('[V212 Deep Link] Error details:', JSON.stringify(error));
          logSupabaseSignInResult(false, error?.message ?? JSON.stringify(error));
          // TODO: Show error to user via toast/notification
          return;
        }

        if (!data.session) {
          clearIOSOAuthLock('exchange_failed_no_session');
          console.error('[V212 Deep Link] ❌ ERROR: Exchange succeeded but no session created');
          logSupabaseSignInResult(false, 'no_session_after_exchange');
          return;
        }

        logSupabaseSignInResult(true);
        clearIOSOAuthLock('exchange_success');

        // V212.3: Diagnostic logging for Android user registration
        const user = data.session.user;
        console.log('[V212 DEBUG] ========================================');
        console.log('[V212 DEBUG] Session User Data:');
        console.log('[V212 DEBUG] - User ID:', user.id);
        console.log('[V212 DEBUG] - Email:', user.email);
        console.log('[V212 DEBUG] - User Metadata:', JSON.stringify(user.user_metadata, null, 2));
        console.log('[V212 DEBUG] - App Metadata:', JSON.stringify(user.app_metadata, null, 2));

        if (user.identities && user.identities.length > 0) {
          console.log('[V212 DEBUG] - Identity Provider:', user.identities[0].provider);
          console.log('[V212 DEBUG] - Identity Data:', JSON.stringify(user.identities[0].identity_data, null, 2));
        }
        console.log('[V212 DEBUG] ========================================');

        // Success!
        console.log('[V212 Deep Link] ✅ SUCCESS: Session established');
        console.log('[V212 Deep Link] User ID:', user.id);
        console.log('[V212 Deep Link] User email:', user.email);
        console.log('[V212 Deep Link] Session expires:', new Date(data.session.expires_at! * 1000).toISOString());
        console.log('[V212 Deep Link] ========================================');

        // V212.3: CRITICAL - Ensure profile is created (Android new user fix)
        console.log('[V212] ========== PROFILE CREATION START ==========');

        try {
          const { ensureUserProfile } = await import('./lib/auth-helpers');
          const profileCreated = await ensureUserProfile(user.id, data.session);

          console.log('[V212] Profile creation result:', profileCreated);

          if (!profileCreated) {
            console.error('[V212] ❌ Profile creation FAILED on first attempt');

            // Retry after 2 seconds
            setTimeout(async () => {
              console.log('[V212] Retrying profile creation...');
              const retryResult = await ensureUserProfile(user.id, data.session);
              console.log('[V212] Retry result:', retryResult);

              if (!retryResult) {
                console.error('[V212] ❌ Profile creation FAILED after retry');
                // Could show error toast to user here
              }
            }, 2000);
          } else {
            console.log('[V212] ✅ Profile creation SUCCEEDED');
          }
        } catch (err) {
          console.error('[V212] ❌ Profile creation EXCEPTION:', err);
          console.error('[V212] Exception details:', err instanceof Error ? err.stack : 'No stack');
        }

        console.log('[V212] ========== PROFILE CREATION END ==========');

        // V212.2: Dispatch custom event for app to handle navigation via router
        // This is cleaner than window.location and allows proper React Router navigation
        const event = new CustomEvent('v212-auth-success', {
          detail: {
            userId: data.session.user.id,
            email: data.session.user.email,
            session: data.session
          }
        });
        window.dispatchEvent(event);
        console.log('[V212 Deep Link] Auth success event dispatched - app will handle navigation');

        // V334: Close SFSafariViewController after successful OAuth callback on iOS.
        // On iOS, custom-scheme redirects (com.blissnet.app://) typically auto-dismiss
        // the in-app browser, but we call Browser.close() explicitly to guarantee clean state.
        if (Capacitor.getPlatform() === 'ios') {
          await closeIOSBrowserSafeMain('oauth_success_callback');
          console.log('[V334 Deep Link] ✅ In-app browser closed after successful OAuth');
        }

      } else {
        console.log('[V284.2 Deep Link] Ignoring unrecognized deep link');
      }
    } catch (err: any) {
      console.error('[V284.2 Deep Link] ❌ UNEXPECTED ERROR:', err);
      console.error('[V284.2 Deep Link] Error stack:', err instanceof Error ? err.stack : 'No stack');
    }
  });

  console.log('[V284.2] ✅ Deep link listener registered at', new Date().toISOString());
}

// V316.7 TASK D: Initialize DEV-only Play Integrity testing tools
if (import.meta.env?.DEV) {
  import('./utils/playIntegrityDevTools').then(({ initPlayIntegrityDevTools }) => {
    initPlayIntegrityDevTools();
  });
}

const root = ReactDOM.createRoot(document.getElementById('root')!);

// V150.16: Direct app render - auth page shows first, splash only for authenticated users
root.render(
  <RouterErrorBoundary>
    <BrowserRouter>
      <App />
      <Toaster
        position="top-center"
        expand={false}
        richColors
        closeButton
        duration={4000}
        toastOptions={{
          duration: 4000,
          style: {
            marginTop: '20px',
            maxWidth: '400px',
          },
        }}
      />
    </BrowserRouter>
  </RouterErrorBoundary>
);

